import { LitElement, html, css } from "lit";
import { customElement, state, property } from "lit/decorators.js";
import { format, startOfDay, endOfDay, subDays } from "date-fns";
import { Router } from "@vaadin/router";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/badge/badge.js";
import "@awesome.me/webawesome/dist/components/spinner/spinner.js";
import "@awesome.me/webawesome/dist/components/dialog/dialog.js";
import "@awesome.me/webawesome/dist/components/textarea/textarea.js";
import "../components/app-shell";
import "../components/cgm-graph";
import type { DebugRenderable } from "../services/debug/debug-service";
import type {
  CgmReading,
  LogEntry,
  PeriodTag,
  UserSettings,
  DayNote,
} from "../types";
import {
  PERIOD_TAG_INFO,
  DIARY_ROW_LABELS,
  DAFNE_LABELS,
  isMarkerPeriod,
  formatInsulinDose,
  getQATotal,
  DEFAULT_SETTINGS,
} from "../types";
import {
  formatRatioDisplay,
  IS_BETA,
  BETA_WARNING,
  getMissingSettings,
} from "../config/dafne";
import { analyzePatterns, type PatternResult } from "../utils/pattern-analysis";
import {
  analyzeDafnePatterns,
  type DafnePattern,
  type DafneSeverity,
} from "../utils/dafne-diary-analysis";
import {
  type HistoryDataAdapter,
  LocalHistoryAdapter,
  LocalMonitoredAdapter,
} from "../services/history-data-adapter";
import { saveDayNote } from "../db/operations";
import { authService } from "../services/auth";
import { syncService, type SyncState } from "../services/sync";
import { monitoredUserSelectionService } from "../services/monitored-user-selection";

type ViewPeriod = "3" | "7" | "14" | "30";
type TimeFilter = "all" | "morning" | "afternoon" | "evening" | "night";

interface DayStats {
  date: Date;
  readings: CgmReading[];
  avg: number;
  min: number;
  max: number;
  tir: number;
  hypos: number;
  highs: number;
}

type HistoryPatternSeverity = "action" | "warning" | "info";

interface HistoryPatternCard {
  source: "diary" | "cgm";
  severity: HistoryPatternSeverity;
  title: string;
  detail: string;
  dafneRecommendation: string;
  affectedPeriods?: PeriodTag[];
  basis?: string;
}

@customElement("history-page")
export class HistoryPage extends LitElement implements DebugRenderable {
  // Data adapter - provides data from different sources
  @property({ type: Object }) dataAdapter?: HistoryDataAdapter;

  @state() private allReadings: CgmReading[] = [];
  @state() private entries: LogEntry[] = [];
  @state() private dayNotes: DayNote[] = [];
  @state() private userSettings: UserSettings = DEFAULT_SETTINGS;
  @state() private loading = true;
  @state() private patterns: HistoryPatternCard[] = [];
  @state() private patternAssessmentSummary = "";
  @state() private patternAssessmentSource: "diary" | "cgm" = "diary";

  @state() private viewPeriod: ViewPeriod = "3";
  @state() private timeFilter: TimeFilter = "all";
  @state() private overlayMode = false;
  @state() private selectedDay: number | null = null;
  @state() private referenceDate: Date = new Date(); // The "end" date for the view

  // Day change detection
  private _dayCheckInterval: ReturnType<typeof setInterval> | null = null;
  private _currentDateKey: string = format(new Date(), "yyyy-MM-dd");
  private _syncUnsubscribe: (() => void) | null = null;

  // Note editing state
  @state() private showNoteDialog = false;
  @state() private editingNoteDay: string | null = null; // yyyy-MM-dd
  @state() private noteText = "";
  @state() private savingNote = false;

  static styles = css`
    /* Box-sizing reset for shadow DOM */
    *,
    *::before,
    *::after {
      box-sizing: border-box;
    }

    :host {
      display: block;
      width: 100%;
    }

    .page-header {
      padding: 1.25rem 1rem 0.5rem;
      background: transparent;
      border-bottom: none;
    }

    .page-header h1 {
      font-size: 1.5rem;
      font-weight: 700;
      margin: 0 0 0.25rem;
      color: var(--color-text, #1f2d3d);
      line-height: 1.15;
    }

    .page-header h1 .accent {
      color: var(--color-accent, #bd64fc);
    }

    .page-header p {
      color: var(--color-text-soft, #6c757d);
      margin: 0;
      font-size: 0.8rem;
    }

    .back-header {
      padding: 0.5rem 1rem;
      background: #fff;
      border-bottom: 1px solid #e9ecef;
    }

    .pill-row {
      display: flex;
      gap: 0.35rem;
      padding: 0.4rem 0.75rem;
      flex-wrap: nowrap;
      align-items: center;
      justify-content: space-between;
    }

    .pill-row .spacer {
      display: none;
    }

    .pill-row .label {
      font-size: 0.7rem;
      color: var(--color-text-soft, #6c757d);
      text-transform: uppercase;
      font-weight: 600;
      margin-right: 0.25rem;
    }

    .pill {
      appearance: none;
      border: 1px solid transparent;
      background: var(--color-card, #d9efff);
      color: var(--color-text, #1f2d3d);
      padding: 0.3rem 0.65rem;
      font-size: 0.75rem;
      font-weight: 600;
      border-radius: var(--radius-pill, 999px);
      cursor: pointer;
      flex: 1 1 0;
      min-width: 0;
      text-align: center;
      white-space: nowrap;
      transition:
        background-color 0.15s,
        color 0.15s;
    }

    .pill:hover {
      background: var(--color-accent-soft, #faf6ff);
    }

    .pill:focus-visible {
      outline: 2px solid var(--color-accent, #bd64fc);
      outline-offset: 2px;
    }

    .pill.active {
      background: var(--color-accent, #bd64fc);
      color: #fff;
    }

    .graph-section {
      margin: 0.5rem 1rem 1rem;
      padding: 1rem;
      background: var(--color-card-surface, #fff);
      border-radius: var(--radius-card, 1rem);
      box-shadow: var(--shadow-card, 0 1px 3px rgba(31, 45, 61, 0.06));
    }

    .graph-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;
    }

    .graph-header h3 {
      font-size: 0.9rem;
      font-weight: 600;
      margin: 0;
      color: var(--color-text, #1f2d3d);
    }

    .stats-row {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 0.5rem;
      padding: 0.5rem 1rem 0.25rem;
    }

    .stat-card {
      text-align: center;
      padding: 0.75rem 0.5rem;
      background: var(--color-card, #d9efff);
      border-radius: var(--radius-stat, 0.9rem);
    }

    .stat-value {
      font-size: 1.25rem;
      font-weight: 700;
      color: #212529;
    }

    .stat-value.good {
      color: #198754;
    }

    .stat-value.warning {
      color: #fd7e14;
    }

    .stat-value.bad {
      color: #dc3545;
    }

    .stat-label {
      font-size: 0.7rem;
      color: #6c757d;
      text-transform: uppercase;
      margin-top: 0.25rem;
    }

    /* Motivation / encouragement card — follows the History theme tokens
       (soft blue surface, accent purple, rounded card chrome). */
    /* Motivation / encouragement — sits inline on the page background,
       no card chrome, blends with the History layout. */
    .motivation-section {
      padding: 1rem 1rem 0.75rem;
      background: transparent;
      border-bottom: none;
    }

    .motivation-card {
      background: transparent;
      border: none;
      box-shadow: none;
      padding: 0;
    }

    .motivation-card .header {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      margin-bottom: 0.25rem;
    }

    .motivation-card .icon {
      font-size: 1.3rem;
    }

    .motivation-card .title {
      font-size: 2rem;
      font-weight: 800;
      color: var(--color-accent, #bd64fc);
      margin: 0;
      line-height: 1.1;
    }

    .motivation-card .message {
      font-size: 0.85rem;
      color: var(--color-text-soft, #6c757d);
      line-height: 1.45;
      margin: 0 0 0.5rem;
    }

    .motivation-card .achievements {
      display: flex;
      flex-wrap: wrap;
      gap: 0.4rem;
    }

    .motivation-card .achievement {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      color: var(--color-text-soft, #6c757d);
      font-size: 0.78rem;
      font-weight: 600;
    }

    .motivation-card .achievement wa-icon {
      font-size: 0.85rem;
      color: var(--color-accent, #bd64fc);
    }

    .daily-breakdown {
      padding: 1rem;
    }

    .daily-breakdown h3 {
      font-size: 0.875rem;
      font-weight: 600;
      margin: 0 0 0.75rem;
      color: #495057;
    }

    .day-row {
      display: grid;
      grid-template-columns: 70px 1fr repeat(4, 38px);
      gap: 0.25rem;
      padding: 0.5rem;
      background: #fff;
      border: 1px solid #e9ecef;
      border-radius: 0.5rem;
      margin-bottom: 0.5rem;
      align-items: center;
      cursor: pointer;
      transition: all 0.15s;
    }

    .day-row:hover {
      background: #f8f9fa;
      border-color: #dee2e6;
    }

    .day-row.selected {
      background: #e7f1ff;
      border-color: #0d6efd;
    }

    .day-row.no-data {
      opacity: 0.5;
      cursor: default;
    }

    .day-date {
      font-weight: 500;
      font-size: 0.75rem;
    }

    .day-date .weekday {
      font-size: 0.65rem;
      color: #6c757d;
      font-weight: normal;
    }

    .day-sparkline {
      height: 20px;
      background: #f8f9fa;
      border-radius: 0.25rem;
      overflow: hidden;
      min-width: 60px;
    }

    .day-stat {
      font-size: 0.7rem;
      text-align: center;
      font-weight: 500;
    }

    .day-stat.avg {
      color: #212529;
    }

    .day-stat.tir {
      color: #198754;
    }

    .day-stat.hypo {
      color: #dc3545;
    }

    .day-stat.high {
      color: #fd7e14;
    }

    .day-detail {
      padding: 1rem;
      background: #f8f9fa;
      margin: -0.5rem 0 0.5rem;
      border-radius: 0 0 0.5rem 0.5rem;
      border: 1px solid #e9ecef;
      border-top: none;
    }

    .readings-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.8rem;
    }

    .readings-table th {
      text-align: left;
      padding: 0.5rem;
      border-bottom: 1px solid #dee2e6;
      color: #6c757d;
      font-weight: 500;
    }

    .readings-table td {
      padding: 0.5rem;
      border-bottom: 1px solid #f0f0f0;
    }

    .reading-badge {
      display: inline-block;
      padding: 0.2rem 0.4rem;
      border-radius: 0.25rem;
      font-weight: 600;
      font-size: 0.75rem;
    }

    .reading-badge.hypo {
      background: #f8d7da;
      color: #842029;
    }

    .reading-badge.low {
      background: #fff3cd;
      color: #664d03;
    }

    .reading-badge.target {
      background: #d1e7dd;
      color: #0f5132;
    }

    .reading-badge.elevated {
      background: #ffe5d0;
      color: #984c0c;
    }

    .reading-badge.high {
      background: #f8d7da;
      color: #842029;
    }

    .no-data {
      text-align: center;
      padding: 3rem 1rem;
      color: #6c757d;
    }

    .no-data wa-icon {
      font-size: 3rem;
      margin-bottom: 1rem;
      display: block;
    }

    .loading {
      display: flex;
      justify-content: center;
      padding: 3rem;
    }

    .summary-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.75rem;
    }

    .summary-header h3 {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 600;
      color: #495057;
    }

    .daily-breakdown .day-header-row {
      display: grid;
      grid-template-columns: 70px 1fr repeat(4, 38px);
      gap: 0.25rem;
      padding: 0.5rem;
      font-size: 0.65rem;
      color: #6c757d;
      text-transform: uppercase;
      font-weight: 600;
    }

    /* DAFNE Diary Table Styles */
    .diary-section {
      padding: 1rem;
      background: #fff;
      width: 100%;
      overflow-x: auto;
    }

    .diary-section h3 {
      font-size: 0.875rem;
      font-weight: 600;
      margin: 0 0 0.75rem;
      color: #495057;
    }

    .table-scroll {
      display: block;
      width: 100%;
      overflow-x: auto;
      overflow-y: visible;
      -webkit-overflow-scrolling: touch;
      padding-bottom: 0.5rem;
    }

    .dafne-diary {
      min-width: 700px;
      border-collapse: collapse;
      font-size: 0.75rem;
      border: 1px solid #dee2e6;
    }

    .dafne-diary th {
      background: #f8f9fa;
      padding: 0.5rem 0.5rem;
      text-align: center;
      font-weight: 600;
      color: #495057;
      border: 1px solid #dee2e6;
      white-space: nowrap;
    }

    .dafne-diary th.date-col {
      width: 80px;
      min-width: 80px;
      text-align: left;
      padding-left: 0.5rem;
    }

    .dafne-diary th.field-col {
      width: 50px;
      min-width: 50px;
    }

    .dafne-diary th.period-col {
      min-width: 55px;
      width: auto;
    }

    .dafne-diary td {
      padding: 0.35rem 0.5rem;
      text-align: right;
      border: 1px solid #e9ecef;
      white-space: nowrap;
    }

    .dafne-diary .date-cell {
      text-align: left;
      padding-left: 0.5rem;
      background: #f8f9fa;
      vertical-align: middle;
    }

    .dafne-diary .day-header-row .date-cell {
      font-weight: 600;
    }

    .dafne-diary .day-header-row .date-cell .day-name {
      display: inline;
    }

    .dafne-diary .day-header-row .date-cell .day-date {
      display: inline;
      margin-left: 0.25rem;
    }

    .dafne-diary .day-name {
      font-weight: 600;
      color: #212529;
    }

    .dafne-diary .day-date {
      font-size: 0.7rem;
      color: #6c757d;
    }

    .dafne-diary .field-label {
      text-align: left;
      padding-left: 0.25rem;
      font-weight: 500;
      color: #6c757d;
      background: #fafafa;
    }

    .dafne-diary .day-start td {
      border-top: 2px solid #dee2e6;
    }

    .dafne-diary .data-cell {
      min-width: 55px;
    }

    /* Marker columns (3am, BF, L, D, BB) */
    .dafne-diary .marker-header {
      background: #f8f9fa;
      font-weight: 600;
    }

    .dafne-diary .marker-cell {
      background: #fff;
    }

    /* Day header row with period labels repeated for each day */
    .dafne-diary .day-header-row td {
      border-top: 2px solid #adb5bd;
      font-size: 0.625rem;
      font-weight: 600;
      color: #495057;
      padding: 0.25rem;
      text-align: center;
    }

    .dafne-diary .day-header-row .period-header {
      background: #f8f9fa;
    }

    .dafne-diary .day-header-row .period-header.other-header {
      background: #e9ecef;
      color: #6c757d;
    }

    .dafne-diary .day-header-row .field-col-header {
      background: #fafafa;
    }

    /* Other columns (entries between markers) */
    .dafne-diary .other-header {
      background: #e9ecef;
      color: #6c757d;
      min-width: 45px;
    }

    .dafne-diary .other-cell {
      background: #f0f0f0;
      border-left: 1px dashed #dee2e6;
      border-right: 1px dashed #dee2e6;
    }

    .dafne-diary .empty {
      color: #dee2e6;
    }

    .dafne-diary .gl-hypo {
      color: #842029;
      background: #f8d7da;
      border-radius: 0.25rem;
      padding: 0.1rem 0.25rem;
    }

    .dafne-diary .gl-low {
      color: #664d03;
      background: #fff3cd;
      border-radius: 0.25rem;
      padding: 0.1rem 0.25rem;
    }

    .dafne-diary .gl-target {
      color: #0f5132;
      background: #d1e7dd;
      border-radius: 0.25rem;
      padding: 0.1rem 0.25rem;
    }

    .dafne-diary .gl-elevated {
      color: #984c0c;
      background: #ffe5d0;
      border-radius: 0.25rem;
      padding: 0.1rem 0.25rem;
    }

    .dafne-diary .gl-high {
      color: #842029;
      background: #f8d7da;
      border-radius: 0.25rem;
      padding: 0.1rem 0.25rem;
    }

    .dafne-diary .cgm-fallback {
      font-style: italic;
      opacity: 0.8;
    }

    .dafne-diary .ratio-row td {
      background: #e7f1ff;
      font-size: 0.7rem;
      padding: 0.25rem 0.5rem;
      color: #0d6efd;
      font-weight: 500;
      text-align: center;
      border-top: 2px solid #0d6efd;
      border-bottom: 2px solid #0d6efd;
    }

    .dafne-diary .ratio-row td.ratio-label {
      text-align: left;
      padding-left: 0.5rem;
      font-weight: 600;
    }

    .dafne-diary .ratio-row td.correction-cell {
      background: #fff3cd;
      color: #664d03;
      border-top-color: #ffc107;
      border-bottom-color: #ffc107;
    }

    .ratio-header {
      background: #e7f1ff;
      padding: 0.75rem 1rem;
      margin-bottom: 0.5rem;
      border-radius: 0.375rem;
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
      align-items: center;
    }

    .ratio-header .ratio-title {
      font-weight: 600;
      font-size: 0.75rem;
      color: #0d6efd;
      text-transform: uppercase;
    }

    .ratio-header .ratio-item {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      font-size: 0.8rem;
    }

    .ratio-header .ratio-period {
      font-weight: 600;
      color: #495057;
    }

    .ratio-header .ratio-value {
      color: #0d6efd;
      font-weight: 500;
    }

    .ratio-header .correction-item {
      background: #fff3cd;
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
      color: #664d03;
    }

    /* Day Notes Styles */
    .day-notes-row td {
      padding: 0.75rem 1rem;
      background: #f8f9fa;
      border-top: 1px dashed #dee2e6;
    }

    .day-notes-container {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .day-note {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      padding: 0.5rem;
      background: #fff;
      border-radius: 0.375rem;
      border: 1px solid #e9ecef;
    }

    .day-note.personal-note {
      border-left: 3px solid #0d6efd;
    }

    .day-note.clinic-note {
      border-left: 3px solid #198754;
      background: #d1e7dd;
    }

    .day-note-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.75rem;
      color: #6c757d;
    }

    .day-note-header .note-type {
      font-weight: 600;
      text-transform: uppercase;
    }

    .day-note-header .note-author {
      color: #495057;
    }

    .day-note-header .note-date {
      margin-left: auto;
    }

    .day-note-content {
      font-size: 0.875rem;
      color: #212529;
      white-space: pre-wrap;
    }

    .add-note-btn {
      font-size: 0.75rem;
      --wa-input-height-small: 1.5rem;
    }

    .note-dialog-date {
      margin: 0 0 1rem;
      color: #6c757d;
      font-size: 0.875rem;
    }

    .no-entries-msg {
      text-align: center;
      padding: 2rem 1rem;
      color: #6c757d;
      background: #f8f9fa;
      border-radius: 0.5rem;
    }

    /* Interleaved Day Card Styles */
    .day-card {
      background: #fff;
      border: 1px solid #e9ecef;
      border-radius: 0.5rem;
      margin: 0 1rem 1rem;
      overflow: hidden;
    }

    .day-card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.75rem 1rem;
      background: #f8f9fa;
      border-bottom: 1px solid #e9ecef;
    }

    .day-card-header .day-title {
      font-weight: 600;
      font-size: 0.9rem;
    }

    .day-card-header .day-date-label {
      font-size: 0.75rem;
      color: #6c757d;
    }

    .day-card-body {
      padding: 0;
    }

    .day-diary-section {
      padding: 0.75rem;
      border-bottom: 1px solid #f0f0f0;
    }

    .day-diary-section .section-label {
      font-size: 0.7rem;
      color: #6c757d;
      text-transform: uppercase;
      font-weight: 600;
      margin-bottom: 0.5rem;
    }

    .day-diary-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.75rem;
    }

    .day-diary-table th {
      background: #f8f9fa;
      padding: 0.375rem 0.25rem;
      text-align: center;
      font-weight: 600;
      color: #495057;
      border-bottom: 1px solid #dee2e6;
    }

    .day-diary-table td {
      padding: 0.25rem;
      text-align: center;
      border-bottom: 1px solid #f0f0f0;
    }

    .day-diary-table .field-label {
      text-align: left;
      padding-left: 0.25rem;
      font-weight: 500;
      color: #6c757d;
      background: #fafafa;
      width: 50px;
    }

    .day-cgm-section {
      padding: 0.75rem;
      background: #fafafa;
    }

    .day-cgm-section .section-label {
      font-size: 0.7rem;
      color: #6c757d;
      text-transform: uppercase;
      font-weight: 600;
      margin-bottom: 0.5rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .day-cgm-stats {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 0.5rem;
      margin-top: 0.5rem;
    }

    .day-cgm-stats .cgm-stat {
      text-align: center;
      padding: 0.5rem;
      background: #fff;
      border-radius: 0.25rem;
      border: 1px solid #e9ecef;
    }

    .day-cgm-stats .cgm-stat-value {
      font-size: 1rem;
      font-weight: 600;
    }

    .day-cgm-stats .cgm-stat-label {
      font-size: 0.625rem;
      color: #6c757d;
      text-transform: uppercase;
    }

    .day-cgm-stats .good {
      color: #198754;
    }
    .day-cgm-stats .warning {
      color: #fd7e14;
    }
    .day-cgm-stats .bad {
      color: #dc3545;
    }

    .day-sparkline-large {
      height: 50px;
      background: #fff;
      border-radius: 0.25rem;
      border: 1px solid #e9ecef;
      overflow: hidden;
    }

    .no-diary-entries {
      font-size: 0.75rem;
      color: #adb5bd;
      font-style: italic;
      padding: 0.5rem 0;
    }

    .no-cgm-data {
      font-size: 0.75rem;
      color: #adb5bd;
      font-style: italic;
      padding: 0.5rem 0;
      text-align: center;
    }

    /* DAFNE Pattern Analysis Styles */
    .patterns-section {
      padding: 1rem;
      background: #fff;
      border-bottom: 1px solid #e9ecef;
    }

    .patterns-section h3 {
      font-size: 0.875rem;
      font-weight: 600;
      margin: 0 0 0.75rem;
      color: #495057;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    .pattern-cards {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      width: 100%;
    }

    .pattern-summary {
      font-size: 0.8rem;
      color: #495057;
      margin: 0 0 0.75rem;
      padding: 0.65rem 0.75rem;
      border-radius: 0.4rem;
      background: #f8f9fa;
      border: 1px solid #e9ecef;
    }

    .pattern-card {
      border: 1px solid #e9ecef;
      border-radius: 0.5rem;
      overflow: hidden;
      width: 100%;
    }

    .pattern-card.severity-action {
      border-color: #f5c6cb;
    }

    .pattern-card.severity-warning {
      border-color: #ffeeba;
    }

    .pattern-card.severity-info {
      border-color: #b8daff;
    }

    .pattern-header {
      padding: 0.75rem;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
    }

    .severity-action .pattern-header {
      background: #f8d7da;
    }

    .severity-warning .pattern-header {
      background: #fff3cd;
    }

    .severity-info .pattern-header {
      background: #d1ecf1;
    }

    .pattern-title {
      font-weight: 600;
      font-size: 0.9rem;
      margin: 0;
    }

    .pattern-badge {
      font-size: 0.65rem;
      padding: 0.2rem 0.5rem;
      border-radius: 0.25rem;
      font-weight: 600;
      text-transform: uppercase;
    }

    .severity-action .pattern-badge {
      background: #dc3545;
      color: white;
    }

    .severity-warning .pattern-badge {
      background: #ffc107;
      color: #212529;
    }

    .severity-info .pattern-badge {
      background: #17a2b8;
      color: white;
    }

    .pattern-body {
      padding: 0.75rem;
      background: #fff;
    }

    .pattern-detail {
      font-size: 0.8rem;
      color: #495057;
      margin: 0 0 0.75rem;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }

    .pattern-basis {
      font-size: 0.72rem;
      color: #6c757d;
      margin: -0.35rem 0 0.75rem;
      padding-left: 0.1rem;
    }

    .pattern-recommendation {
      font-size: 0.8rem;
      background: #f8f9fa;
      padding: 0.75rem;
      border-radius: 0.375rem;
      border-left: 3px solid #0d6efd;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }

    .pattern-recommendation strong {
      display: block;
      font-size: 0.7rem;
      text-transform: uppercase;
      color: #0d6efd;
      margin-bottom: 0.25rem;
    }

    .pattern-affected {
      display: flex;
      gap: 0.25rem;
      margin-top: 0.5rem;
      flex-wrap: wrap;
    }

    .pattern-period-tag {
      font-size: 0.65rem;
      padding: 0.15rem 0.4rem;
      background: #e9ecef;
      border-radius: 0.25rem;
      color: #495057;
    }

    .no-patterns {
      text-align: center;
      padding: 1.5rem;
      color: #6c757d;
      background: #f8f9fa;
      border-radius: 0.5rem;
      font-size: 0.85rem;
    }

    .no-patterns wa-icon {
      font-size: 2rem;
      margin-bottom: 0.5rem;
      display: block;
    }

    /* Ratios bar */
  `;

  async connectedCallback() {
    super.connectedCallback();

    // For monitor-only users, use a local DB adapter (data already downloaded)
    if (!this.dataAdapter && monitoredUserSelectionService.isMonitorOnly) {
      await monitoredUserSelectionService.whenLoaded();
      await monitoredUserSelectionService.whenDataReady();
      const sel = monitoredUserSelectionService.selected;
      const mdb = monitoredUserSelectionService.db;
      if (sel && mdb) {
        this.dataAdapter = new LocalMonitoredAdapter(
          mdb,
          sel.userId,
          sel.displayName,
          sel.email,
          sel.relationshipType,
        );
      }
    }

    // Set default view period based on context
    // 3 days for patient view, 7 days for clinic view
    if (this.dataAdapter) {
      this.viewPeriod = "7";
    }

    // CRITICAL: Wait for initial sync to complete before loading data
    // This prevents redirecting to settings with defaults on a new device
    if (!this.dataAdapter) {
      await this._waitForInitialSync();
    }

    await this._loadData();

    // Start day change detection - check every 30 seconds
    this._startDayChangeDetection();
  }

  /**
   * Wait for initial sync to complete (or timeout/go offline)
   */
  private async _waitForInitialSync(): Promise<void> {
    const currentState = syncService.state;
    if (currentState.initialSyncComplete || currentState.isConfirmedFreshUser) {
      return;
    }

    if (!navigator.onLine) {
      return;
    }

    if (currentState.status === "idle") {
      syncService.checkAndSync();
    }

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        console.warn(
          "[HistoryPage] Initial sync timeout - proceeding without sync",
        );
        if (this._syncUnsubscribe) {
          this._syncUnsubscribe();
          this._syncUnsubscribe = null;
        }
        resolve();
      }, 15000);

      this._syncUnsubscribe = syncService.subscribe((state: SyncState) => {
        if (
          state.initialSyncComplete ||
          state.isConfirmedFreshUser ||
          state.status === "error" ||
          state.status === "offline"
        ) {
          clearTimeout(timeout);
          if (this._syncUnsubscribe) {
            this._syncUnsubscribe();
            this._syncUnsubscribe = null;
          }
          resolve();
        }
      });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._stopDayChangeDetection();
    if (this._syncUnsubscribe) {
      this._syncUnsubscribe();
      this._syncUnsubscribe = null;
    }
  }

  private _startDayChangeDetection() {
    // Check every 30 seconds if the day has changed
    this._dayCheckInterval = setInterval(() => {
      const newDateKey = format(new Date(), "yyyy-MM-dd");
      if (newDateKey !== this._currentDateKey) {
        console.log(
          `Day changed from ${this._currentDateKey} to ${newDateKey}, refreshing...`,
        );
        this._currentDateKey = newDateKey;
        // Update reference date to today and reload
        this.referenceDate = new Date();
        this._loadData();
      }
    }, 30000);
  }

  private _stopDayChangeDetection() {
    if (this._dayCheckInterval) {
      clearInterval(this._dayCheckInterval);
      this._dayCheckInterval = null;
    }
  }

  private async _loadData(days?: number) {
    this.loading = true;
    try {
      // Use adapter if provided, otherwise use default local adapter
      const adapter = this.dataAdapter || new LocalHistoryAdapter();
      // Pass days to adapter for lazy loading (default to viewPeriod)
      const requestDays = days ?? parseInt(this.viewPeriod);
      const { readings, entries, settings, dayNotes } =
        await adapter.loadData(requestDays);
      const resolvedSettings = settings || DEFAULT_SETTINGS;

      // Require core DAFNE settings before allowing primary local history view
      if (!this.dataAdapter) {
        const missing = getMissingSettings(resolvedSettings);
        if (missing.length > 0) {
          this.loading = false;
          Router.go("/settings?required=1&from=history");
          return;
        }
      }

      this.allReadings = readings;
      this.entries = entries;
      this.dayNotes = dayNotes || [];
      this.userSettings = resolvedSettings;

      // Calculate data date range from both CGM readings and entries
      let minTs = Infinity;
      let maxTs = -Infinity;

      for (const r of readings) {
        const ts = this._getTimestamp(r.timestamp);
        if (ts < minTs) minTs = ts;
        if (ts > maxTs) maxTs = ts;
      }

      for (const e of entries) {
        const ts = this._getTimestamp(e.timestamp);
        if (ts < minTs) minTs = ts;
        if (ts > maxTs) maxTs = ts;
      }

      if (minTs !== Infinity && maxTs !== -Infinity) {
        // Always use today as reference date (user wants to see current day)
        // Data range just tells us what data exists, not what we should show
        const today = new Date();
        this.referenceDate = today;
        this._currentDateKey = format(today, "yyyy-MM-dd");
      }

      // Run pattern analysis
      this._updatePatterns();
    } catch (error) {
      console.error("Failed to load data:", error);
    }
    this.loading = false;
  }

  private _mapDiarySeverity(severity: DafneSeverity): HistoryPatternSeverity {
    if (severity === "action") return "action";
    if (severity === "monitor") return "warning";
    return "info";
  }

  private _mapDiaryPattern(pattern: DafnePattern): HistoryPatternCard {
    return {
      source: "diary",
      severity: this._mapDiarySeverity(pattern.severity),
      title: pattern.title,
      detail: pattern.detail,
      dafneRecommendation: pattern.recommendation,
      affectedPeriods: pattern.affectedPeriod ? [pattern.affectedPeriod] : [],
      basis: `Pattern seen on ${pattern.data.daysAffected} of ${pattern.data.totalDays} logged days.`,
    };
  }

  private _mapCgmPattern(pattern: PatternResult): HistoryPatternCard {
    return {
      source: "cgm",
      severity: pattern.severity,
      title: pattern.title,
      detail: pattern.detail,
      dafneRecommendation: pattern.dafneRecommendation,
      affectedPeriods: pattern.affectedPeriods,
      basis: "CGM trend model fallback (limited diary pattern data).",
    };
  }

  private _updatePatterns() {
    const daysToAnalyze = parseInt(this.viewPeriod);
    const diaryAnalysis = analyzeDafnePatterns(
      this.entries,
      daysToAnalyze,
      this.userSettings,
    );

    const daysWithDiaryData = diaryAnalysis.analysisWindow.daysWithData;
    const hasSufficientDiaryData = daysWithDiaryData >= 2;

    if (hasSufficientDiaryData) {
      this.patterns = diaryAnalysis.patterns.map((p) =>
        this._mapDiaryPattern(p),
      );
      this.patternAssessmentSource = "diary";
      this.patternAssessmentSummary =
        `${diaryAnalysis.overallAssessment} ` +
        `Based on ${daysWithDiaryData} day(s) with diary readings in the last ${daysToAnalyze} day(s).`;
      return;
    }

    const cgmPatterns = analyzePatterns({
      cgmReadings: this.allReadings,
      entries: this.entries,
      daysToAnalyze,
      targetLow: 4,
      targetHigh: 10,
    });
    this.patterns = cgmPatterns.map((p) => this._mapCgmPattern(p));
    this.patternAssessmentSource = "cgm";
    this.patternAssessmentSummary =
      daysWithDiaryData === 0
        ? "No diary readings in this window yet. Showing CGM trend analysis as fallback."
        : `Only ${daysWithDiaryData} day(s) with diary readings in this window. Showing CGM trend analysis fallback.`;
  }

  private _getTimestamp(t: Date | string | number | unknown): number {
    if (t instanceof Date) return t.getTime();
    if (typeof t === "string") return new Date(t).getTime();
    if (typeof t === "number") return t;
    return new Date(t as string).getTime();
  }

  private _getFilteredReadings(): CgmReading[] {
    const days = parseInt(this.viewPeriod);
    const refDate = this.referenceDate;
    const startTs = startOfDay(subDays(refDate, days - 1)).getTime();
    const endTs = endOfDay(refDate).getTime();

    return this.allReadings.filter((r) => {
      const ts = this._getTimestamp(r.timestamp);
      if (ts < startTs || ts > endTs) return false;

      // Time of day filter
      if (this.timeFilter !== "all") {
        const date = new Date(ts);
        const hour = date.getHours();

        switch (this.timeFilter) {
          case "morning": // 6-12
            if (hour < 6 || hour >= 12) return false;
            break;
          case "afternoon": // 12-18
            if (hour < 12 || hour >= 18) return false;
            break;
          case "evening": // 18-22
            if (hour < 18 || hour >= 22) return false;
            break;
          case "night": // 22-6
            if (hour >= 6 && hour < 22) return false;
            break;
        }
      }

      return true;
    });
  }

  private _getReadingsForDay(dayOffset: number): CgmReading[] {
    const dayDate = subDays(this.referenceDate, dayOffset);
    const dayStart = startOfDay(dayDate).getTime();
    const dayEnd = endOfDay(dayDate).getTime();

    return this.allReadings
      .filter((r) => {
        const ts = this._getTimestamp(r.timestamp);
        if (ts < dayStart || ts > dayEnd) return false;

        // Apply time filter
        if (this.timeFilter !== "all") {
          const date = new Date(ts);
          const hour = date.getHours();

          switch (this.timeFilter) {
            case "morning":
              if (hour < 6 || hour >= 12) return false;
              break;
            case "afternoon":
              if (hour < 12 || hour >= 18) return false;
              break;
            case "evening":
              if (hour < 18 || hour >= 22) return false;
              break;
            case "night":
              if (hour >= 6 && hour < 22) return false;
              break;
          }
        }

        return true;
      })
      .sort(
        (a, b) =>
          this._getTimestamp(a.timestamp) - this._getTimestamp(b.timestamp),
      );
  }

  /**
   * Get the CGM reading closest to 3AM for a given day.
   * Used as fallback when no DAFNE entry exists for the 3AM period.
   * Returns null if no CGM readings exist for that day.
   */
  private _get3amCgmReading(dayOffset: number): CgmReading | null {
    const dayDate = subDays(this.referenceDate, dayOffset);
    const target3am = new Date(dayDate);
    target3am.setHours(3, 0, 0, 0);
    const target3amTs = target3am.getTime();

    // Get all readings for the day
    const dayStart = startOfDay(dayDate).getTime();
    const dayEnd = endOfDay(dayDate).getTime();

    const dayReadings = this.allReadings.filter((r) => {
      const ts = this._getTimestamp(r.timestamp);
      return ts >= dayStart && ts <= dayEnd;
    });

    if (dayReadings.length === 0) return null;

    // Find closest reading to 3AM
    let closest: CgmReading | null = null;
    let minDiff = Infinity;

    for (const reading of dayReadings) {
      const ts = this._getTimestamp(reading.timestamp);
      const diff = Math.abs(ts - target3amTs);
      if (diff < minDiff) {
        minDiff = diff;
        closest = reading;
      }
    }

    return closest;
  }

  private _calculatePeriodStats(readings: CgmReading[]) {
    if (readings.length === 0) {
      return { avg: 0, min: 0, max: 0, tir: 0, hypos: 0, highs: 0, count: 0 };
    }

    const values = readings.map((r) => r.glucose);
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    const inRange = values.filter((v) => v >= 4 && v <= 10).length;

    return {
      avg,
      min: Math.min(...values),
      max: Math.max(...values),
      tir: Math.round((inRange / values.length) * 100),
      hypos: values.filter((v) => v < 4).length,
      highs: values.filter((v) => v > 10).length,
      count: values.length,
    };
  }

  private _getDayStats(dayOffset: number): DayStats {
    const readings = this._getReadingsForDay(dayOffset);
    const stats = this._calculatePeriodStats(readings);

    return {
      date: subDays(this.referenceDate, dayOffset),
      readings,
      ...stats,
    };
  }

  private _getGlClass(value?: number): string {
    if (!value) return "";
    if (value < 4) return "gl-hypo";
    if (value < 5) return "gl-low";
    if (value <= 7) return "gl-target";
    if (value <= 10) return "gl-elevated";
    return "gl-high";
  }

  private _getEntriesForDay(dayOffset: number): LogEntry[] {
    const dayDate = subDays(this.referenceDate, dayOffset);
    const dayStart = startOfDay(dayDate);
    const dayEnd = endOfDay(dayDate);

    return this.entries
      .filter((e) => {
        const ts = this._getTimestamp(e.timestamp);
        return ts >= dayStart.getTime() && ts <= dayEnd.getTime();
      })
      .sort(
        (a, b) =>
          this._getTimestamp(a.timestamp) - this._getTimestamp(b.timestamp),
      );
  }

  private _getNotesForDay(dayOffset: number): DayNote[] {
    const dayDate = subDays(this.referenceDate, dayOffset);
    const dateKey = format(dayDate, "yyyy-MM-dd");

    return this.dayNotes
      .filter((n) => n.date === dateKey)
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
  }

  private _openAddNoteDialog(dayOffset: number) {
    const dayDate = subDays(this.referenceDate, dayOffset);
    this.editingNoteDay = format(dayDate, "yyyy-MM-dd");
    this.noteText = "";
    this.showNoteDialog = true;
  }

  private _closeNoteDialog() {
    this.showNoteDialog = false;
    this.editingNoteDay = null;
    this.noteText = "";
  }

  private async _handleSaveNote() {
    if (!this.editingNoteDay || !this.noteText.trim()) return;

    const authState = await authService.getCurrentUser();
    if (!authState.isAuthenticated || !authState.userId) return;

    this.savingNote = true;
    try {
      // Use adapter's saveNote if available (for monitored users)
      if (this.dataAdapter?.saveNote) {
        await this.dataAdapter.saveNote(
          this.editingNoteDay,
          this.noteText.trim(),
        );
      } else {
        // Save to local database (own data)
        await saveDayNote({
          date: this.editingNoteDay,
          type: "personal",
          content: this.noteText.trim(),
          authorId: authState.userId,
          authorName: authState.displayName || authState.email || "Me",
        });
      }

      // Refresh day notes
      const adapter = this.dataAdapter || new LocalHistoryAdapter();
      const data = await adapter.loadData();
      this.dayNotes = data.dayNotes || [];

      this._closeNoteDialog();
    } catch (error) {
      console.error("Failed to save note:", error);
    } finally {
      this.savingNote = false;
    }
  }

  private async _handleViewChange(period: ViewPeriod) {
    this.viewPeriod = period;
    this.selectedDay = null;

    // If using lazy loading adapter and need more data, fetch it
    const days = parseInt(period);
    const adapter = this.dataAdapter;
    if (adapter?.supportsLazyLoad && (adapter.loadedDays || 0) < days) {
      // Need to fetch more data
      await this._loadData(days);
    } else {
      // Just update patterns with existing data
      this._updatePatterns();
    }
  }

  private _handleTimeFilter(filter: TimeFilter) {
    this.timeFilter = filter;
  }

  private _renderStats() {
    const readings = this._getFilteredReadings();
    const hasData = readings.length > 0;
    const stats = hasData
      ? this._calculatePeriodStats(readings)
      : { avg: 0, tir: 0, hypos: 0, highs: 0 };
    const placeholder = (value: string) => (hasData ? value : "—");

    return html`
      <div class="stats-row">
        <div class="stat-card">
          <div
            class="stat-value ${!hasData
              ? ""
              : stats.avg >= 5 && stats.avg <= 8
                ? "good"
                : stats.avg < 4 || stats.avg > 12
                  ? "bad"
                  : "warning"}"
          >
            ${placeholder(stats.avg.toFixed(1))}
          </div>
          <div class="stat-label">Avg mmol/L</div>
        </div>
        <div class="stat-card">
          <div
            class="stat-value ${!hasData
              ? ""
              : stats.tir >= 70
                ? "good"
                : stats.tir >= 50
                  ? "warning"
                  : "bad"}"
          >
            ${placeholder(`${stats.tir}%`)}
          </div>
          <div class="stat-label">TIR 4-10</div>
        </div>
        <div class="stat-card">
          <div
            class="stat-value ${!hasData
              ? ""
              : stats.hypos === 0
                ? "good"
                : "bad"}"
          >
            ${placeholder(String(stats.hypos))}
          </div>
          <div class="stat-label">Hypos &lt;4</div>
        </div>
        <div class="stat-card">
          <div
            class="stat-value ${!hasData
              ? ""
              : stats.highs <= 5
                ? "good"
                : stats.highs <= 15
                  ? "warning"
                  : "bad"}"
          >
            ${placeholder(String(stats.highs))}
          </div>
          <div class="stat-label">Highs &gt;10</div>
        </div>
      </div>
    `;
  }

  /**
   * Render motivation/positive feedback when the patient is doing well.
   * Shows achievements and encouraging messages based on their data.
   */
  private _renderMotivation() {
    const readings = this._getFilteredReadings();
    const days = parseInt(this.viewPeriod);
    const totalEntries = this.entries.length;

    // ----- Empty / early-stage encouragement tiers -----
    // The motivation panel is supposed to feel like a friendly nudge, so we
    // always render *something* — even when there's no data — instead of
    // hiding it. Tier is decided by how much logging the user has done.

    if (readings.length === 0 && totalEntries === 0) {
      return this._renderMotivationCard({
        emoji: "🌱",
        title: "Let's Get Started",
        message:
          "Your DAFNE journey begins with a single entry. Logging meals, insulin, and glucose readings is what makes the patterns and insights on this page come to life.",
        achievements: [
          { icon: "plus", text: "Add your first entry" },
          { icon: "cloud-arrow-up", text: "Or import CGM data" },
        ],
      });
    }

    if (readings.length < 10) {
      // Some entries logged but not yet enough data for trend analysis.
      let daysWithEntries = 0;
      for (let i = 0; i < days; i++) {
        const dayEntries = this._getEntriesForDay(i);
        if (dayEntries.length > 0) daysWithEntries++;
      }
      const achievements: { icon: string; text: string }[] = [];
      if (totalEntries > 0)
        achievements.push({
          icon: "pen-to-square",
          text: `${totalEntries} ${totalEntries === 1 ? "entry" : "entries"} logged`,
        });
      if (daysWithEntries > 0)
        achievements.push({
          icon: "calendar-check",
          text: `${daysWithEntries}/${days} days started`,
        });
      if (readings.length > 0)
        achievements.push({
          icon: "droplet",
          text: `${readings.length} GL ${readings.length === 1 ? "reading" : "readings"}`,
        });
      return this._renderMotivationCard({
        emoji: "🌟",
        title: "Good Start!",
        message:
          "You've started logging — that's the hardest step. Keep going for a few more days and DAFNE pattern insights will start appearing here.",
        achievements,
      });
    }

    const stats = this._calculatePeriodStats(readings);

    // Calculate achievements
    const achievements: { icon: string; text: string }[] = [];

    // TIR achievement
    if (stats.tir >= 80) {
      achievements.push({
        icon: "trophy",
        text: `Excellent TIR: ${stats.tir}%`,
      });
    } else if (stats.tir >= 70) {
      achievements.push({
        icon: "circle-check",
        text: `Great TIR: ${stats.tir}%`,
      });
    }

    // Average GL achievement
    if (stats.avg >= 5.0 && stats.avg <= 7.0) {
      achievements.push({
        icon: "bullseye",
        text: `Avg in target: ${stats.avg.toFixed(1)} mmol/L`,
      });
    }

    // No hypos achievement
    if (stats.hypos === 0 && readings.length > 20) {
      achievements.push({ icon: "shield-check", text: "No hypos" });
    }

    // Few highs achievement
    if (stats.highs <= 3 && readings.length > 20) {
      achievements.push({ icon: "arrow-down", text: "Minimal highs" });
    }

    // Logging consistency - check if entries exist for each day
    let daysWithEntries = 0;
    for (let i = 0; i < days; i++) {
      const dayEntries = this._getEntriesForDay(i);
      if (dayEntries.length > 0) daysWithEntries++;
    }
    const loggingPercent = Math.round((daysWithEntries / days) * 100);
    if (loggingPercent >= 80) {
      achievements.push({
        icon: "book",
        text: `Logged ${daysWithEntries}/${days} days`,
      });
    }

    // No earned achievements yet → still show a "Making Progress" nudge so
    // the panel never goes silent on a user who is logging.
    if (achievements.length === 0) {
      return this._renderMotivationCard({
        emoji: "💪",
        title: "Making Progress",
        message:
          "You're logging consistently — that's the foundation. The patterns below will help you spot what to tweak next.",
        achievements: [
          { icon: "pen-to-square", text: `${totalEntries} entries logged` },
        ],
      });
    }

    // Generate encouraging message
    let message = "";
    if (achievements.length >= 4) {
      message =
        "You're doing fantastic! Your consistent logging and great numbers show real commitment to your DAFNE skills. Keep up the excellent work!";
    } else if (achievements.length >= 2) {
      message =
        "Great progress! You're building good habits. Keep logging regularly and these improvements will continue.";
    } else {
      message =
        "You're making progress! Every good reading and logged entry helps you understand your patterns better.";
    }

    return this._renderMotivationCard({
      emoji: "🌟",
      title: "You're Doing Great!",
      message,
      achievements,
    });
  }

  private _renderMotivationCard(opts: {
    emoji: string;
    title: string;
    message: string;
    achievements: { icon: string; text: string }[];
  }) {
    return html`
      <div class="motivation-section">
        <div class="motivation-card">
          <div class="header">
            <span class="icon">${opts.emoji}</span>
            <h4 class="title">${opts.title}</h4>
          </div>
          <p class="message">${opts.message}</p>
          ${opts.achievements.length > 0
            ? html`
                <div class="achievements">
                  ${opts.achievements.map(
                    (a) => html`
                      <span class="achievement">
                        <wa-icon name=${a.icon}></wa-icon>
                        ${a.text}
                      </span>
                    `,
                  )}
                </div>
              `
            : ""}
        </div>
      </div>
    `;
  }

  private _renderPatterns() {
    if (this.patterns.length === 0) {
      return html`
        <div class="patterns-section">
          <h3>
            <wa-icon name="clipboard-list"></wa-icon>
            DAFNE Pattern Analysis
            <wa-badge
              variant=${this.patternAssessmentSource === "diary"
                ? "success"
                : "warning"}
              pill
              >${this.patternAssessmentSource === "diary"
                ? "Diary"
                : "CGM Fallback"}</wa-badge
            >
          </h3>
          <div class="no-patterns">
            <wa-icon name="circle-check"></wa-icon>
            <p>No significant patterns detected.</p>
            <p>
              ${this.patternAssessmentSummary ||
              "Continue logging to enable pattern detection."}
            </p>
          </div>
        </div>
      `;
    }

    return html`
      <div class="patterns-section">
        <h3>
          <wa-icon name="clipboard-list"></wa-icon>
          DAFNE Pattern Analysis
          <wa-badge variant="brand" pill>${this.patterns.length}</wa-badge>
          <wa-badge
            variant=${this.patternAssessmentSource === "diary"
              ? "success"
              : "warning"}
            pill
            >${this.patternAssessmentSource === "diary"
              ? "Diary"
              : "CGM Fallback"}</wa-badge
          >
        </h3>
        ${IS_BETA
          ? html`<div
              class="beta-warning"
              style="margin-bottom: 1rem; font-size: 0.8rem; color: #856404; background: #fff3cd; padding: 0.5rem 0.75rem; border-radius: 6px; border: 1px solid #ffeeba;"
            >
              ⚠️ <strong>Beta:</strong> ${BETA_WARNING.SHORT}
            </div>`
          : ""}
        ${this.patternAssessmentSummary
          ? html`<p class="pattern-summary">
              ${this.patternAssessmentSummary}
            </p>`
          : ""}
        <div class="pattern-cards">
          ${this.patterns.map(
            (pattern) => html`
              <div class="pattern-card severity-${pattern.severity}">
                <div class="pattern-header">
                  <p class="pattern-title">${pattern.title}</p>
                  <span class="pattern-badge">${pattern.severity}</span>
                </div>
                <div class="pattern-body">
                  <p class="pattern-detail">${pattern.detail}</p>
                  ${pattern.basis
                    ? html`<p class="pattern-basis">${pattern.basis}</p>`
                    : ""}
                  <div class="pattern-recommendation">
                    <strong>DAFNE Action</strong>
                    ${pattern.dafneRecommendation}
                  </div>
                  ${pattern.affectedPeriods &&
                  pattern.affectedPeriods.length > 0
                    ? html`
                        <div class="pattern-affected">
                          ${pattern.affectedPeriods.map(
                            (period) => html`
                              <span class="pattern-period-tag">
                                ${PERIOD_TAG_INFO[period]?.label || period}
                              </span>
                            `,
                          )}
                        </div>
                      `
                    : ""}
                </div>
              </div>
            `,
          )}
        </div>
      </div>
    `;
  }

  private _renderGraph() {
    const readings = this._getFilteredReadings();
    const days = parseInt(this.viewPeriod);
    const dateRange = {
      start: startOfDay(subDays(this.referenceDate, days - 1)),
      end: endOfDay(this.referenceDate),
    };

    // Get entries for the period
    const startTs = dateRange.start.getTime();
    const endTs = dateRange.end.getTime();
    const periodEntries = this.entries.filter((e) => {
      const ts = this._getTimestamp(e.timestamp);
      return ts >= startTs && ts <= endTs;
    });

    return html`
      <div class="graph-section">
        <div class="graph-header">
          <h3>Glucose Trend - Last ${this.viewPeriod} Days</h3>
          <wa-badge variant="neutral">${readings.length} readings</wa-badge>
        </div>
        <cgm-graph
          .readings=${readings}
          .entries=${periodEntries}
          .dateRange=${dateRange}
          .mode=${this.overlayMode ? "overlay" : "straight"}
          height=${200}
        ></cgm-graph>
      </div>
    `;
  }

  /**
   * Build columns for the DAFNE diary table.
   *
   * The diary has 5 fixed marker columns (3am, BF, L, D, BB) that always show
   * and align across all days. "Other" entries (non-marker entries) are placed
   * in columns between markers based on their actual time.
   *
   * Column layout: | (other) | 3am | (other) | BF | (other) | L | (other) | D | (other) | BB | (other) |
   */
  private _buildDynamicColumns(): {
    period: PeriodTag | "Other";
    index: number;
    label: string;
    isMarker: boolean;
    slotPosition: number; // 0-5 for marker slots, -1 for other columns
  }[] {
    const days = parseInt(this.viewPeriod);

    // Get the hour slot an entry belongs to (for placing Other entries)
    const getSlotForHour = (hour: number): number => {
      // Returns 0-4 for the slot after each marker
      // Slot 0: before 3am (0:00-1:59)
      // Slot 1: after 3am, before BF (would be empty as 3am covers 2-4)
      // Slot 2: after BF, before L
      // etc.
      if (hour >= 0 && hour < 2) return 0; // Before 3am marker
      if (hour >= 2 && hour < 5) return 1; // 3am slot
      if (hour >= 5 && hour < 11) return 2; // BF slot
      if (hour >= 11 && hour < 15) return 3; // L slot
      if (hour >= 15 && hour < 21) return 4; // D slot
      return 5; // BB slot (21:00-23:59)
    };

    // Count max "Other" entries per slot across all days
    // Slots: 0=before 3am, 1=after 3am/before BF, 2=after BF/before L, etc.
    const maxOtherPerSlot: number[] = [0, 0, 0, 0, 0, 0];

    for (let dayOffset = 0; dayOffset < days; dayOffset++) {
      const dayEntries = this._getEntriesForDay(dayOffset);
      const slotCounts = [0, 0, 0, 0, 0, 0];

      for (const entry of dayEntries) {
        // Count non-marker entries (Other and legacy after-* tags)
        if (!isMarkerPeriod(entry.periodTag)) {
          const hour = new Date(this._getTimestamp(entry.timestamp)).getHours();
          const slot = getSlotForHour(hour);
          slotCounts[slot]++;
        }
      }

      // Update max counts
      for (let i = 0; i < 6; i++) {
        if (slotCounts[i] > maxOtherPerSlot[i]) {
          maxOtherPerSlot[i] = slotCounts[i];
        }
      }
    }

    // Build columns: interleave Other columns between markers
    const columns: {
      period: PeriodTag | "Other";
      index: number;
      label: string;
      isMarker: boolean;
      slotPosition: number;
    }[] = [];

    // Slot 0: Other entries before 3am (00:00-01:59)
    for (let i = 0; i < maxOtherPerSlot[0]; i++) {
      columns.push({
        period: "Other",
        index: i,
        label: "",
        isMarker: false,
        slotPosition: 0,
      });
    }

    // 3am marker
    columns.push({
      period: "3AM",
      index: 0,
      label: PERIOD_TAG_INFO["3AM"]?.label || "3am",
      isMarker: true,
      slotPosition: 1,
    });

    // Slot 1: No Other entries possible here (3am covers 2:00-4:59)
    // Skip slot 1

    // BF marker
    columns.push({
      period: "B",
      index: 0,
      label: PERIOD_TAG_INFO["B"]?.label || "Breakfast",
      isMarker: true,
      slotPosition: 2,
    });

    // Slot 2: Other entries after BF, before L (5:00-10:59 is BF, so no gap)
    // Actually BF covers 5-10, L starts at 11, so Other entries in slot 2 would be... none
    // Wait, I need to reconsider. "Other" entries are entries with periodTag="Other" or after-*
    // They get placed in the column based on their time, not the marker time.

    // Actually, let me reconsider the slots:
    // Slot 0: 00:00-01:59 (before 3am marker)
    // Slot 1: 02:00-04:59 (3am marker's time window - but Other entries here go before BF)
    // Slot 2: 05:00-10:59 (BF marker's time window)
    // Slot 3: 11:00-14:59 (L marker's time window)
    // Slot 4: 15:00-20:59 (D marker's time window)
    // Slot 5: 21:00-23:59 (BB marker's time window)

    // "Other" entries during a marker's time window go in a column right after that marker

    // Let me simplify: Other columns appear after the marker whose time window they fall in

    // Slot 2: Other entries during BF time (already counted)
    for (let i = 0; i < maxOtherPerSlot[2]; i++) {
      columns.push({
        period: "Other",
        index: i,
        label: "",
        isMarker: false,
        slotPosition: 2,
      });
    }

    // L marker
    columns.push({
      period: "L",
      index: 0,
      label: PERIOD_TAG_INFO["L"]?.label || "Lunch",
      isMarker: true,
      slotPosition: 3,
    });

    // Slot 3: Other entries during L time
    for (let i = 0; i < maxOtherPerSlot[3]; i++) {
      columns.push({
        period: "Other",
        index: i,
        label: "",
        isMarker: false,
        slotPosition: 3,
      });
    }

    // D marker
    columns.push({
      period: "EM",
      index: 0,
      label: PERIOD_TAG_INFO["EM"]?.label || "Dinner",
      isMarker: true,
      slotPosition: 4,
    });

    // Slot 4: Other entries during D time
    for (let i = 0; i < maxOtherPerSlot[4]; i++) {
      columns.push({
        period: "Other",
        index: i,
        label: "",
        isMarker: false,
        slotPosition: 4,
      });
    }

    // BB marker
    columns.push({
      period: "BT",
      index: 0,
      label: PERIOD_TAG_INFO["BT"]?.label || "Before Bed",
      isMarker: true,
      slotPosition: 5,
    });

    // Slot 5: Other entries during BB time
    for (let i = 0; i < maxOtherPerSlot[5]; i++) {
      columns.push({
        period: "Other",
        index: i,
        label: "",
        isMarker: false,
        slotPosition: 5,
      });
    }

    return columns;
  }

  private _toggleDayDetail(dayOffset: number, hasData: boolean) {
    if (!hasData) return;
    this.selectedDay = this.selectedDay === dayOffset ? null : dayOffset;
  }

  private _getReadingClass(value: number): string {
    if (value < 4) return "hypo";
    if (value < 5) return "low";
    if (value <= 7) return "target";
    if (value <= 10) return "elevated";
    return "high";
  }

  private _renderDiaryTable() {
    const days = parseInt(this.viewPeriod);
    const columns = this._buildDynamicColumns();
    const dataRows = [...DIARY_ROW_LABELS];
    const settings = this.userSettings;

    // Get unique periods that have ratio columns (exclude 3AM which doesn't have CP ratio)
    const ratioPeriods = ["B", "L", "EM"] as const;

    // Build a list of entries sorted by time to detect ratio changes
    const allEntriesInPeriod = this.entries
      .filter((e) => {
        const ts = this._getTimestamp(e.timestamp);
        const start = startOfDay(
          subDays(this.referenceDate, days - 1),
        ).getTime();
        const end = endOfDay(this.referenceDate).getTime();
        return ts >= start && ts <= end;
      })
      .sort(
        (a, b) =>
          this._getTimestamp(b.timestamp) - this._getTimestamp(a.timestamp),
      );

    // Detect ratio changes - build a map of date -> ratios used that day
    interface RatioSnapshot {
      cpRatios: Record<string, number | undefined>;
      correctionFactor: number | undefined;
    }

    const ratioByDay = new Map<string, RatioSnapshot>();
    for (const entry of allEntriesInPeriod) {
      const dayKey = format(
        new Date(this._getTimestamp(entry.timestamp)),
        "yyyy-MM-dd",
      );
      if (!ratioByDay.has(dayKey)) {
        ratioByDay.set(dayKey, {
          cpRatios: {},
          correctionFactor: entry.correctionFactorUsed,
        });
      }
      const snapshot = ratioByDay.get(dayKey)!;
      if (entry.cpRatioUsed !== undefined && entry.periodTag) {
        const ratioKey =
          PERIOD_TAG_INFO[entry.periodTag]?.ratioFrom || entry.periodTag;
        if (!snapshot.cpRatios[ratioKey]) {
          snapshot.cpRatios[ratioKey] = entry.cpRatioUsed;
        }
      }
      if (
        entry.correctionFactorUsed !== undefined &&
        !snapshot.correctionFactor
      ) {
        snapshot.correctionFactor = entry.correctionFactorUsed;
      }
    }

    // Helper to get the time slot for an hour (for Other entry placement)
    const getSlotForHour = (hour: number): number => {
      if (hour >= 0 && hour < 2) return 0; // Before 3am
      if (hour >= 2 && hour < 5) return 1; // 3am slot
      if (hour >= 5 && hour < 11) return 2; // BF slot
      if (hour >= 11 && hour < 15) return 3; // L slot
      if (hour >= 15 && hour < 21) return 4; // D slot
      return 5; // BB slot
    };

    // Helper to get entry data for a day/column combination
    const getEntryField = (
      dayOffset: number,
      col: {
        period: PeriodTag | "Other";
        index: number;
        isMarker: boolean;
        slotPosition: number;
      },
      field: string,
    ) => {
      const dayEntries = this._getEntriesForDay(dayOffset);

      let entry: LogEntry | undefined;

      if (col.isMarker) {
        // For marker columns, find the entry with matching periodTag
        entry = dayEntries.find((e) => e.periodTag === col.period);

        // For 3am marker with no entry, try CGM fallback
        if (!entry && col.period === "3AM") {
          const cgmReading = this._get3amCgmReading(dayOffset);
          if (cgmReading) {
            switch (field) {
              case DAFNE_LABELS.TIME:
                return html`<span class="cgm-fallback"
                  >${format(
                    new Date(this._getTimestamp(cgmReading.timestamp)),
                    "HH:mm",
                  )}</span
                >`;
              case DAFNE_LABELS.GL:
                return html`<span
                  class="cgm-fallback ${this._getGlClass(cgmReading.glucose)}"
                  >${cgmReading.glucose.toFixed(1)}</span
                >`;
              default:
                return html`<span class="empty">-</span>`;
            }
          }
        }
      } else {
        // For Other columns, find entries that are not markers and match the slot position
        const otherEntries = dayEntries
          .filter((e) => !isMarkerPeriod(e.periodTag))
          .filter((e) => {
            const hour = new Date(this._getTimestamp(e.timestamp)).getHours();
            return getSlotForHour(hour) === col.slotPosition;
          })
          .sort(
            (a, b) =>
              this._getTimestamp(a.timestamp) - this._getTimestamp(b.timestamp),
          );
        entry = otherEntries[col.index];
      }

      if (!entry) return html`<span class="empty">-</span>`;

      switch (field) {
        case DAFNE_LABELS.TIME:
          return entry.timestamp
            ? format(new Date(this._getTimestamp(entry.timestamp)), "HH:mm")
            : "-";
        case DAFNE_LABELS.GL:
          return entry.bloodGlucose
            ? html`<span class=${this._getGlClass(entry.bloodGlucose)}
                >${entry.bloodGlucose.toFixed(1)}</span
              >`
            : "-";
        case DAFNE_LABELS.CP:
          return entry.carbs ? `${entry.carbs}` : "-";
        case DAFNE_LABELS.QUICK:
          const qa = getQATotal(entry);
          return qa !== undefined
            ? formatInsulinDose(entry.qaBase, entry.qaCorrection)
            : "-";
        case DAFNE_LABELS.LONG:
          return entry.biBase !== undefined ? `${entry.biBase}` : "-";
        default:
          return "-";
      }
    };

    // Render ratio header showing current settings
    const renderRatioHeader = () => {
      const hasRatios =
        ratioPeriods.some((p) => settings.cpRatios[p] != null) ||
        settings.correctionFactor != null;

      if (!hasRatios) return "";

      return html`
        <div class="ratio-header">
          <span class="ratio-title">Current Ratios:</span>
          ${ratioPeriods.map((period) => {
            const unitsPerCP = settings.cpRatios[period];
            if (unitsPerCP == null) return "";
            return html`
              <span class="ratio-item">
                <span class="ratio-period"
                  >${PERIOD_TAG_INFO[period]?.label || period}:</span
                >
                <span class="ratio-value"
                  >${formatRatioDisplay(unitsPerCP)}</span
                >
              </span>
            `;
          })}
          ${settings.correctionFactor != null
            ? html`
                <span class="ratio-item correction-item">
                  <span class="ratio-period">ISF:</span>
                  <span class="ratio-value"
                    >${settings.correctionFactor} mmol/L/u</span
                  >
                </span>
              `
            : ""}
        </div>
      `;
    };

    // Render a ratio change row in the table
    const renderRatioRow = (snapshot: RatioSnapshot, colCount: number) => {
      const formatRatioValue = (unitsPerCP: number) => {
        return Number.isInteger(unitsPerCP)
          ? unitsPerCP.toString()
          : unitsPerCP.toFixed(1);
      };

      const ratioText = ratioPeriods
        .filter((p) => snapshot.cpRatios[p] != null)
        .map(
          (p) =>
            `${PERIOD_TAG_INFO[p]?.label || p}: ${formatRatioValue(snapshot.cpRatios[p]!)}u`,
        )
        .join(" | ");

      const correctionText =
        snapshot.correctionFactor != null
          ? `ISF: ${snapshot.correctionFactor}`
          : "";

      return html`
        <tr class="ratio-row">
          <td class="ratio-label" colspan="2">Ratios Used</td>
          ${ratioText
            ? html`<td colspan="${Math.floor((colCount - 1) / 2)}">
                ${ratioText}
              </td>`
            : ""}
          ${correctionText
            ? html`<td
                class="correction-cell"
                colspan="${colCount - Math.floor((colCount - 1) / 2) - 1}"
              >
                ${correctionText}
              </td>`
            : html`<td
                colspan="${colCount - Math.floor((colCount - 1) / 2) - 1}"
              ></td>`}
        </tr>
      `;
    };

    // Track previous day's ratios to detect changes
    let prevRatioSnapshot: RatioSnapshot | null = null;

    return html`
      <div class="diary-section">
        <h3>DAFNE Diary - ${this.viewPeriod} Days</h3>
        ${renderRatioHeader()}
        <div class="table-scroll">
          <table class="dafne-diary">
            <tbody>
              ${Array.from({ length: days }, (_, dayOffset) => {
                const dayDate = subDays(this.referenceDate, dayOffset);
                const dayKey = format(dayDate, "yyyy-MM-dd");
                const dayLabel =
                  dayOffset === 0
                    ? "Today"
                    : dayOffset === 1
                      ? "Yesterday"
                      : format(dayDate, "EEE");
                const dateLabel = format(dayDate, "d MMM");

                // Check if ratios changed from previous day
                const currentSnapshot = ratioByDay.get(dayKey);
                let showRatioChange = false;

                if (currentSnapshot && prevRatioSnapshot) {
                  // Check if any ratio differs
                  const cpChanged = ratioPeriods.some(
                    (p) =>
                      currentSnapshot.cpRatios[p] !==
                        prevRatioSnapshot!.cpRatios[p] &&
                      (currentSnapshot.cpRatios[p] != null ||
                        prevRatioSnapshot!.cpRatios[p] != null),
                  );
                  const corrChanged =
                    currentSnapshot.correctionFactor !==
                      prevRatioSnapshot.correctionFactor &&
                    (currentSnapshot.correctionFactor != null ||
                      prevRatioSnapshot.correctionFactor != null);

                  showRatioChange = cpChanged || corrChanged;
                }

                // Update previous snapshot for next iteration
                if (currentSnapshot) {
                  prevRatioSnapshot = currentSnapshot;
                }

                return html`
                  ${showRatioChange && currentSnapshot
                    ? renderRatioRow(currentSnapshot, columns.length)
                    : ""}
                  <tr class="day-header-row">
                    <td class="date-cell" colspan="2">
                      <div class="day-name">${dayLabel}</div>
                      <div class="day-date">${dateLabel}</div>
                    </td>
                    ${columns.map(
                      (col) =>
                        html`<td
                          class="period-header ${col.isMarker
                            ? "marker-header"
                            : "other-header"}"
                        >
                          ${col.label}
                        </td>`,
                    )}
                  </tr>
                  ${dataRows.map(
                    (field, fieldIdx) => html`
                      <tr
                        class=${fieldIdx === dataRows.length - 1
                          ? "day-end"
                          : ""}
                      >
                        <td
                          class="field-label ${field.toLowerCase()}"
                          colspan="2"
                        >
                          ${field}
                        </td>
                        ${columns.map(
                          (col) => html`
                            <td
                              class="data-cell ${field.toLowerCase()} ${col.isMarker
                                ? "marker-cell"
                                : "other-cell"}"
                            >
                              ${getEntryField(dayOffset, col, field)}
                            </td>
                          `,
                        )}
                      </tr>
                    `,
                  )}
                  ${this._renderDayNotes(dayOffset, columns.length + 2)}
                `;
              })}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }

  private _renderDayNotes(dayOffset: number, colCount: number) {
    const notes = this._getNotesForDay(dayOffset);
    const isOwnData = !this.dataAdapter; // No adapter means viewing own data
    const canAddNotes = isOwnData || this.dataAdapter?.canAddNotes;

    // Show if there are notes OR if user can add notes
    if (notes.length === 0 && !canAddNotes) {
      return "";
    }

    return html`
      <tr class="day-notes-row">
        <td colspan="${colCount}">
          <div class="day-notes-container">
            ${notes.map(
              (note) => html`
                <div class="day-note ${note.type}-note">
                  <div class="day-note-header">
                    <span class="note-type">
                      <wa-icon
                        name="${note.type === "clinic" ? "hospital" : "person"}"
                      ></wa-icon>
                      ${note.type === "clinic"
                        ? "Clinic Note"
                        : "Personal Note"}
                    </span>
                    ${note.authorName
                      ? html`<span class="note-author"
                          >by ${note.authorName}</span
                        >`
                      : ""}
                    <span class="note-date"
                      >${format(new Date(note.createdAt), "d MMM HH:mm")}</span
                    >
                  </div>
                  <div class="day-note-content">${note.content}</div>
                </div>
              `,
            )}
            ${canAddNotes
              ? html`
                  <wa-button
                    size="small"
                    variant="text"
                    class="add-note-btn"
                    @click=${() => this._openAddNoteDialog(dayOffset)}
                  >
                    <wa-icon slot="prefix" name="circle-plus"></wa-icon>
                    ${isOwnData ? "Add Note" : "Add Clinic Note"}
                  </wa-button>
                `
              : ""}
          </div>
        </td>
      </tr>
    `;
  }

  private _renderDailyBreakdown() {
    if (this.allReadings.length === 0) {
      return ""; // No CGM data to show
    }

    const days = parseInt(this.viewPeriod);

    return html`
      <div class="daily-breakdown">
        <div class="summary-header">
          <h3>CGM Daily Summary</h3>
        </div>
        <div class="day-header-row">
          <div>Date</div>
          <div>Trend</div>
          <div>Avg</div>
          <div>TIR</div>
          <div>Low</div>
          <div>High</div>
        </div>
        ${Array.from({ length: days }, (_, i) => this._renderDayRow(i))}
      </div>
    `;
  }

  private _renderDayRow(dayOffset: number) {
    const stats = this._getDayStats(dayOffset);
    const hasData = stats.readings.length > 0;
    const isSelected = this.selectedDay === dayOffset;
    const isToday = dayOffset === 0;

    return html`
      <div
        class="day-row ${isSelected ? "selected" : ""} ${!hasData
          ? "no-data"
          : ""}"
        @click=${() => this._toggleDayDetail(dayOffset, hasData)}
      >
        <div class="day-date">
          ${isToday ? "Today" : format(stats.date, "d MMM")}
          <span class="weekday">${format(stats.date, "EEE")}</span>
        </div>
        <div class="day-sparkline">
          ${hasData ? this._renderMiniSparkline(stats.readings) : ""}
        </div>
        <div class="day-stat avg">${hasData ? stats.avg.toFixed(1) : "-"}</div>
        <div class="day-stat tir">${hasData ? `${stats.tir}%` : "-"}</div>
        <div class="day-stat hypo">${hasData ? stats.hypos : "-"}</div>
        <div class="day-stat high">${hasData ? stats.highs : "-"}</div>
      </div>
      ${isSelected ? this._renderDayDetail(stats) : ""}
    `;
  }

  private _renderMiniSparkline(readings: CgmReading[]) {
    if (readings.length < 2) return "";

    // Create a simple SVG sparkline
    const width = 100;
    const height = 24;
    const padding = 2;

    const values = readings.map((r) => r.glucose);
    const min = Math.min(...values, 2);
    const max = Math.max(...values, 20);
    const range = max - min || 1;

    const points = readings.map((r, i) => {
      const x = padding + (i / (readings.length - 1)) * (width - 2 * padding);
      const y =
        height - padding - ((r.glucose - min) / range) * (height - 2 * padding);
      return `${x},${y}`;
    });

    // Target range band (4-10 mmol/L)
    const targetTop =
      height - padding - ((10 - min) / range) * (height - 2 * padding);
    const targetBottom =
      height - padding - ((4 - min) / range) * (height - 2 * padding);

    return html`
      <svg
        width="100%"
        height="${height}"
        viewBox="0 0 ${width} ${height}"
        preserveAspectRatio="none"
      >
        <rect
          x="${padding}"
          y="${Math.max(padding, targetTop)}"
          width="${width - 2 * padding}"
          height="${Math.min(height - 2 * padding, targetBottom - targetTop)}"
          fill="#d1e7dd"
          opacity="0.5"
        />
        <polyline
          fill="none"
          stroke="#0d6efd"
          stroke-width="1.5"
          points="${points.join(" ")}"
        />
      </svg>
    `;
  }

  private _renderDayDetail(stats: DayStats) {
    // Show hourly summary
    const hourlyData = new Map<
      number,
      { readings: CgmReading[]; avg: number }
    >();

    for (const r of stats.readings) {
      const hour = new Date(this._getTimestamp(r.timestamp)).getHours();
      if (!hourlyData.has(hour)) {
        hourlyData.set(hour, { readings: [], avg: 0 });
      }
      hourlyData.get(hour)!.readings.push(r);
    }

    // Calculate hourly averages
    for (const [, data] of hourlyData) {
      const values = data.readings.map((r) => r.glucose);
      data.avg = values.reduce((a, b) => a + b, 0) / values.length;
    }

    // Sort by hour
    const sortedHours = Array.from(hourlyData.entries()).sort(
      (a, b) => a[0] - b[0],
    );

    return html`
      <div class="day-detail">
        <div class="summary-header">
          <h3>Hourly Breakdown</h3>
          <wa-badge variant="neutral"
            >${stats.readings.length} readings</wa-badge
          >
        </div>
        <table class="readings-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Readings</th>
              <th>Avg</th>
              <th>Range</th>
            </tr>
          </thead>
          <tbody>
            ${sortedHours.map(([hour, data]) => {
              const values = data.readings.map((r) => r.glucose);
              const min = Math.min(...values);
              const max = Math.max(...values);
              return html`
                <tr>
                  <td>${hour.toString().padStart(2, "0")}:00</td>
                  <td>${data.readings.length}</td>
                  <td>
                    <span
                      class="reading-badge ${this._getReadingClass(data.avg)}"
                    >
                      ${data.avg.toFixed(1)}
                    </span>
                  </td>
                  <td>${min.toFixed(1)} - ${max.toFixed(1)}</td>
                </tr>
              `;
            })}
          </tbody>
        </table>
      </div>
    `;
  }

  /**
   * Generate markdown representation of current page state for debugging
   * Renders DAFNE diary tables in markdown format - exact match to UI table structure
   */
  renderMarkdown(): string {
    const lines: string[] = [];
    const adapter = this.dataAdapter;
    const days = parseInt(this.viewPeriod);

    lines.push(`## History`);
    lines.push("");
    lines.push(`**View Period:** ${this.viewPeriod} days`);
    lines.push(`**Time Filter:** ${this.timeFilter}`);
    lines.push(
      `**Reference Date:** ${format(this.referenceDate, "yyyy-MM-dd")}`,
    );
    if (adapter?.title) {
      lines.push(`**Adapter Title:** ${adapter.title}`);
    }
    lines.push("");

    // CGM Stats
    if (this.allReadings.length > 0) {
      const values = this.allReadings.map((r) => r.glucose);
      const avg = values.reduce((a, b) => a + b, 0) / values.length;
      const min = Math.min(...values);
      const max = Math.max(...values);
      const tirCount = values.filter((v) => v >= 4 && v <= 10).length;
      const tir = (tirCount / values.length) * 100;
      const hypoCount = values.filter((v) => v < 4).length;
      const highCount = values.filter((v) => v > 10).length;

      lines.push("### CGM Statistics");
      lines.push("");
      lines.push(`- **Total Readings:** ${this.allReadings.length}`);
      lines.push(`- **Average:** ${avg.toFixed(1)} mmol/L`);
      lines.push(`- **Range:** ${min.toFixed(1)} - ${max.toFixed(1)} mmol/L`);
      lines.push(`- **Time in Range (4-10):** ${tir.toFixed(0)}%`);
      lines.push(`- **Hypos (<4):** ${hypoCount}`);
      lines.push(`- **Highs (>10):** ${highCount}`);
      lines.push("");
    }

    // Patterns
    if (this.patterns.length > 0) {
      lines.push("### Pattern Analysis");
      lines.push("");
      if (this.patternAssessmentSummary) {
        lines.push(`**Summary:** ${this.patternAssessmentSummary}`);
        lines.push("");
      }
      for (const pattern of this.patterns) {
        const severity =
          pattern.severity === "action"
            ? "[ACTION]"
            : pattern.severity === "warning"
              ? "[WARNING]"
              : "[INFO]";
        lines.push(`- ${severity} **${pattern.title}**: ${pattern.detail}`);
        if (pattern.dafneRecommendation) {
          lines.push(
            `  - DAFNE Recommendation: ${pattern.dafneRecommendation}`,
          );
        }
      }
      lines.push("");
    }

    // DAFNE Diary Table - exact same structure as UI
    lines.push("### DAFNE Diary");
    lines.push("");

    // Build dynamic columns using exact same logic as _renderDiaryTable
    const columns = this._buildDynamicColumns();

    // Helper to determine slot for a given hour (same as getSlotForHour in _renderDiaryTable)
    const getSlotForHour = (hour: number): number => {
      if (hour >= 0 && hour < 2) return 0; // Pre-3AM slot
      if (hour >= 2 && hour < 5) return 1; // 3AM (2:00-4:59)
      if (hour >= 5 && hour < 11) return 2; // Breakfast (5:00-10:59)
      if (hour >= 11 && hour < 15) return 3; // Lunch (11:00-14:59)
      if (hour >= 15 && hour < 21) return 4; // Dinner/EM (15:00-20:59)
      return 5; // Bedtime (21:00-23:59)
    };

    // Helper to get entry field as text (mirrors getEntryField logic from _renderDiaryTable)
    const getEntryFieldText = (
      dayOffset: number,
      col: {
        period: PeriodTag | "Other";
        index: number;
        isMarker: boolean;
        slotPosition: number;
      },
      field: string,
    ): string => {
      const dayEntries = this._getEntriesForDay(dayOffset);

      let entry: LogEntry | undefined;

      if (col.isMarker) {
        // For marker columns, find the entry with matching periodTag
        entry = dayEntries.find((e) => e.periodTag === col.period);

        // For 3am marker with no entry, try CGM fallback
        if (!entry && col.period === "3AM") {
          const cgmReading = this._get3amCgmReading(dayOffset);
          if (cgmReading) {
            switch (field) {
              case DAFNE_LABELS.TIME:
                return format(
                  new Date(this._getTimestamp(cgmReading.timestamp)),
                  "HH:mm",
                );
              case DAFNE_LABELS.GL:
                return cgmReading.glucose.toFixed(1);
              default:
                return "-";
            }
          }
        }
      } else {
        // For Other columns, find entries that are not markers and match the slot position
        const otherEntries = dayEntries
          .filter((e) => !isMarkerPeriod(e.periodTag))
          .filter((e) => {
            const hour = new Date(this._getTimestamp(e.timestamp)).getHours();
            return getSlotForHour(hour) === col.slotPosition;
          })
          .sort(
            (a, b) =>
              this._getTimestamp(a.timestamp) - this._getTimestamp(b.timestamp),
          );
        entry = otherEntries[col.index];
      }

      if (!entry) return "-";

      switch (field) {
        case DAFNE_LABELS.TIME:
          return entry.timestamp
            ? format(new Date(this._getTimestamp(entry.timestamp)), "HH:mm")
            : "-";
        case DAFNE_LABELS.GL:
          return entry.bloodGlucose ? entry.bloodGlucose.toFixed(1) : "-";
        case DAFNE_LABELS.CP:
          return entry.carbs ? `${entry.carbs}` : "-";
        case DAFNE_LABELS.QUICK:
          const qa = getQATotal(entry);
          return qa !== undefined
            ? formatInsulinDose(entry.qaBase, entry.qaCorrection)
            : "-";
        case DAFNE_LABELS.LONG:
          return entry.biBase !== undefined ? `${entry.biBase}` : "-";
        default:
          return "-";
      }
    };

    // Header row - exact same column labels as UI
    const headerCells = columns.map((col) => col.label);
    lines.push(`| Row | ${headerCells.join(" | ")} |`);
    lines.push(`|-----|${columns.map(() => "-----").join("|")}|`);

    // Data rows for each day
    for (let dayOffset = 0; dayOffset < days; dayOffset++) {
      const dayDate = subDays(this.referenceDate, dayOffset);
      const dateStr = format(dayDate, "EEE d MMM");

      // Row label shows the date
      const dateLabel = dayOffset === 0 ? `**${dateStr}**` : dateStr;

      // Time row
      const timeRow = columns.map((col) =>
        getEntryFieldText(dayOffset, col, DAFNE_LABELS.TIME),
      );
      lines.push(`| ${dateLabel} Time | ${timeRow.join(" | ")} |`);

      // GL row
      const glRow = columns.map((col) =>
        getEntryFieldText(dayOffset, col, DAFNE_LABELS.GL),
      );
      lines.push(`| GL | ${glRow.join(" | ")} |`);

      // CP row
      const cpRow = columns.map((col) =>
        getEntryFieldText(dayOffset, col, DAFNE_LABELS.CP),
      );
      lines.push(`| CP | ${cpRow.join(" | ")} |`);

      // Quick row
      const quickRow = columns.map((col) =>
        getEntryFieldText(dayOffset, col, DAFNE_LABELS.QUICK),
      );
      lines.push(`| Quick | ${quickRow.join(" | ")} |`);

      // Long row
      const longRow = columns.map((col) =>
        getEntryFieldText(dayOffset, col, DAFNE_LABELS.LONG),
      );
      lines.push(`| Long | ${longRow.join(" | ")} |`);

      lines.push(`|------|${columns.map(() => "-----").join("|")}|`);
    }

    lines.push("");

    // Notes section
    for (let dayOffset = 0; dayOffset < days; dayOffset++) {
      const dayNotes = this._getNotesForDay(dayOffset);
      if (dayNotes.length > 0) {
        const dayDate = subDays(this.referenceDate, dayOffset);
        const dateStr = format(dayDate, "EEE d MMM");
        lines.push(`**Notes (${dateStr}):**`);
        for (const note of dayNotes) {
          lines.push(`- ${note.content}`);
        }
        lines.push("");
      }
    }

    return lines.join("\n");
  }

  render() {
    // Monitor-only with no person selected after load — redirect to person selector
    if (
      !this.dataAdapter &&
      monitoredUserSelectionService.isMonitorOnly &&
      monitoredUserSelectionService.loaded &&
      !monitoredUserSelectionService.selected
    ) {
      Router.go("/monitoring");
      return html``;
    }

    const adapter = this.dataAdapter;
    const pageTitle = adapter?.title || "History";
    const pageSubtitle = adapter?.subtitle;
    const hasBackButton = !!adapter?.onBack;

    if (this.loading) {
      return html`
        <app-shell pageTitle=${pageTitle}>
          ${hasBackButton
            ? html`
                <div class="back-header">
                  <wa-button variant="text" @click=${() => adapter?.onBack?.()}>
                    <wa-icon slot="prefix" name="arrow-left"></wa-icon>
                    Back
                  </wa-button>
                </div>
              `
            : ""}
          <div class="loading">
            <wa-spinner style="font-size: 2rem;"></wa-spinner>
          </div>
        </app-shell>
      `;
    }

    return html`
      <app-shell pageTitle=${pageTitle}>
        ${hasBackButton
          ? html`
              <div class="back-header">
                <wa-button variant="text" @click=${() => adapter?.onBack?.()}>
                  <wa-icon slot="prefix" name="arrow-left"></wa-icon>
                  Back
                </wa-button>
              </div>
            `
          : ""}
        <div class="page-header">
          <h1>
            ${adapter?.title
              ? html`${adapter.title}<span class="accent" style="display:block">History</span>`
              : html`Let's <span class="accent">take a look</span>`}
          </h1>
          ${pageSubtitle ? html`<p>${pageSubtitle}</p>` : ""}
        </div>

        <div class="pill-row">
          <button
            class="pill ${this.viewPeriod === "3" ? "active" : ""}"
            @click=${() => this._handleViewChange("3")}
            title="DAFNE assessment: 2+ days consistency before ratio changes"
          >
            3 Days
          </button>
          <button
            class="pill ${this.viewPeriod === "7" ? "active" : ""}"
            @click=${() => this._handleViewChange("7")}
          >
            7 Days
          </button>
          <button
            class="pill ${this.viewPeriod === "14" ? "active" : ""}"
            @click=${() => this._handleViewChange("14")}
          >
            14 Days
          </button>
          <button
            class="pill ${this.viewPeriod === "30" ? "active" : ""}"
            @click=${() => this._handleViewChange("30")}
          >
            30 Days
          </button>
          <span class="spacer"></span>
          <button
            class="pill ${this.overlayMode ? "active" : ""}"
            @click=${() => (this.overlayMode = !this.overlayMode)}
          >
            Overlay
          </button>
        </div>

        <div class="pill-row">
          <button
            class="pill ${this.timeFilter === "all" ? "active" : ""}"
            @click=${() => this._handleTimeFilter("all")}
          >
            All Day
          </button>
          <button
            class="pill ${this.timeFilter === "morning" ? "active" : ""}"
            @click=${() => this._handleTimeFilter("morning")}
          >
            Morning
          </button>
          <button
            class="pill ${this.timeFilter === "afternoon" ? "active" : ""}"
            @click=${() => this._handleTimeFilter("afternoon")}
          >
            Afternoon
          </button>
          <button
            class="pill ${this.timeFilter === "evening" ? "active" : ""}"
            @click=${() => this._handleTimeFilter("evening")}
          >
            Evening
          </button>
          <button
            class="pill ${this.timeFilter === "night" ? "active" : ""}"
            @click=${() => this._handleTimeFilter("night")}
          >
            Night
          </button>
        </div>

        ${this._renderMotivation()} ${this._renderStats()}
        ${this._renderGraph()} ${this._renderPatterns()}
        ${this._renderDiaryTable()} ${this._renderDailyBreakdown()}

        <!-- Add Note Dialog -->
        <wa-dialog
          label="${this.dataAdapter ? "Add Clinic Note" : "Add Note"}"
          ?open=${this.showNoteDialog}
          @wa-after-hide=${() => this._closeNoteDialog()}
        >
          ${this.editingNoteDay
            ? html`<p class="note-dialog-date">
                ${this.dataAdapter
                  ? html`Clinic note for ${this.dataAdapter.title} — `
                  : "Note for "}
                ${format(
                  new Date(this.editingNoteDay + "T12:00:00"),
                  "EEEE, d MMMM yyyy",
                )}
              </p>`
            : ""}
          <wa-textarea
            placeholder="${this.dataAdapter
              ? "Add a clinic note for this patient's day..."
              : "Add your note about this day..."}"
            rows="4"
            .value=${this.noteText}
            @input=${(e: Event) =>
              (this.noteText = (e.target as HTMLInputElement).value)}
          ></wa-textarea>
          <div slot="footer">
            <wa-button
              variant="neutral"
              @click=${() => this._closeNoteDialog()}
            >
              Cancel
            </wa-button>
            <wa-button
              variant="brand"
              ?loading=${this.savingNote}
              ?disabled=${!this.noteText.trim()}
              @click=${() => this._handleSaveNote()}
            >
              Save Note
            </wa-button>
          </div>
        </wa-dialog>
      </app-shell>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "history-page": HistoryPage;
  }
}
